/**
 * Utility to create lightweight separators for flex layouts.
 * Useful when borders would complicate paddings/margins.
 */

export type SeparatorOrientation = "vertical" | "horizontal";

export interface SeparatorConfig {
  readonly orientation?: SeparatorOrientation; // Default: 'vertical'
  readonly thickness?: number | string; // px number or CSS length. Default: 1
  readonly length?: "full" | number | string; // 'full' stretches, or explicit length
  readonly color?: string; // CSS color or var(). Default: var(--color-border)
  readonly opacity?: number; // 0..1. Default: 1
  readonly className?: string; // Optional extra classes
}

/**
 * Create a separator element configured for flex layouts.
 * - Vertical: width = thickness, height = full (stretch) or custom length
 * - Horizontal: height = thickness, width = full (100%) or custom length
 */
export const createSeparator = (config: SeparatorConfig = {}): HTMLElement => {
  const {
    orientation = "vertical",
    thickness = 1,
    length = "full",
    color = "var(--color-border)",
    opacity = 1,
    className = "",
  } = config;

  const el = document.createElement("div");
  el.setAttribute("role", "presentation");
  el.className = ["ui-separator", `ui-separator--${orientation}`, className]
    .filter(Boolean)
    .join(" ");

  const toCssLength = (value: number | string): string =>
    typeof value === "number" ? `${value}px` : value;

  // Common styling
  el.style.backgroundColor = color;
  el.style.opacity = String(opacity);
  el.style.flexShrink = "0";
  el.style.pointerEvents = "none";

  if (orientation === "vertical") {
    el.style.width = toCssLength(thickness);

    if (length === "full") {
      // Stretch to container cross-size within a row flex
      el.style.alignSelf = "stretch";
      el.style.height = "100%";
    } else {
      el.style.height = toCssLength(length);
    }
  } else {
    // horizontal
    el.style.height = toCssLength(thickness);

    if (length === "full") {
      // Fill available main-axis width in column flex
      el.style.width = "100%";
      el.style.alignSelf = "stretch";
    } else {
      el.style.width = toCssLength(length);
    }
  }

  return el;
};

/**
 * Convenience helper: create and append to container.
 */
export const createSeparatorInContainer = (
  container: HTMLElement,
  config: SeparatorConfig = {}
): HTMLElement => {
  const sep = createSeparator(config);
  container.appendChild(sep);
  return sep;
};
